import { setTag } from '@sentry/react';
import { useInsights } from 'contexts/insights';
import { getLocale } from 'i18n/i18nUtils';
import { useEffect } from 'react';
import { useAppSelector } from 'redux-store/hooks';
import { selectOrientation, selectShouldUseMobileLayout } from 'redux-store/rootReducer';
import { AppStateEnum, QUERY_PARAMS, SentryTags, WebGLContext } from 'utils/constants';
import { CURRENT_RUNNING_TEST } from 'utils/constants/abTestSettings';
import { getBrowserTheme } from 'utils/getBrowserTheme';
import { DeviceTypeEnum, getPlatform, isIpad, isIpexGallery, isUpptacka } from 'utils/platform';
import { cleanVersion, isVerboseSentry } from 'utils/sentry';
import { supportsWebp } from 'utils/supportsWebp';
import useQuery from 'utils/useQuery';
import packageInfo from '../../package.json';
import { useABTest } from './useABtest';

/**
 * Initial data added during setup. Sets data on the isolation scope.
 * https://docs.sentry.io/platforms/javascript/enriching-events/scopes/
 */
export const setInitialTags = async () => {
  if (isVerboseSentry()) {
    setTag(SentryTags.verboseSentry, true);
    // eslint-disable-next-line no-console
    console.log('Verbose logging rates has been set.');
  }
  setTag(QUERY_PARAMS.LOCALE, getPlatform());
  setTag(QUERY_PARAMS.LOCALE, getLocale());
  setTag(QUERY_PARAMS.STORE_ID, useQuery(QUERY_PARAMS.STORE_ID));
  setTag(QUERY_PARAMS.COMPUTER_ID, useQuery(QUERY_PARAMS.COMPUTER_ID));
  setTag(SentryTags.isUpptacka, isUpptacka());
  setTag(SentryTags.isIpexGallery, isIpexGallery());
  setTag(SentryTags.browserTheme, getBrowserTheme());
  setTag(SentryTags.webpSupport, await supportsWebp());
  setTag(SentryTags.appState, AppStateEnum.Initial);
  setTag(SentryTags.coreVersion, cleanVersion(packageInfo.dependencies['@inter-ikea-core/core']));
  setTag(
    SentryTags.betraktaVersion,
    cleanVersion(packageInfo.dependencies['@inter-ikea-betrakta/viewer'])
  );
  setTag(
    SentryTags.kompisVersion,
    cleanVersion(packageInfo.dependencies['@inter-ikea-kompis/react-components'])
  );
  setTag(
    SentryTags.insightsVersion,
    cleanVersion(packageInfo.dependencies['@insights/insights-data-provider'])
  );
  if (CURRENT_RUNNING_TEST) {
    setTag(SentryTags.abTest, CURRENT_RUNNING_TEST);

    const { version } = useABTest(CURRENT_RUNNING_TEST);
    setTag(SentryTags.abVariation, version);
  }
  setTag(SentryTags.webglContext, WebGLContext.None);
  setTag(SentryTags.insightsIpexId, 'notConnected');
  setTag(SentryTags.insightsLongTermId, 'notConnected');
  setTag(SentryTags.insightsSessionId, 'notConnected');
};

/**
 * Contextual data added during application setup.
 * Can be further improved by adding more context to events:
 * https://docs.sentry.io/platforms/javascript/enriching-events/context/
 */
export const useSentryTags = () => {
  const orientation = useAppSelector(selectOrientation);
  const { connected, insightsApi } = useInsights();
  const isMobile = useAppSelector(selectShouldUseMobileLayout);

  const setOrientationBasedSentryTags = () => {
    setTag(SentryTags.orientation, orientation);
    setTag(SentryTags.screenHeightPixels, window.innerHeight);
    setTag(SentryTags.screenWidthPixels, window.innerWidth);
  };

  const deviceType = isIpad()
    ? DeviceTypeEnum.Tablet
    : isMobile
      ? DeviceTypeEnum.Mobile
      : DeviceTypeEnum.Desktop;

  useEffect(() => {
    setOrientationBasedSentryTags();
    setTag(SentryTags.deviceType, deviceType);
  }, []);

  useEffect(() => {
    setOrientationBasedSentryTags();
  }, [orientation]);

  useEffect(() => {
    setTag(SentryTags.insightsConnected, connected);

    if (!connected) {
      return;
    }

    const ids = insightsApi.getIds();

    if (!ids) {
      return;
    }

    const { ipexId, longTermId, sessionId } = ids;

    setTag(SentryTags.insightsIpexId, ipexId);
    setTag(SentryTags.insightsLongTermId, longTermId);
    setTag(SentryTags.insightsSessionId, sessionId);
  }, [connected]);
};
